import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMG } from "@/data/content";
import { trackEvent } from "@/lib/tracking";
import LatestVanguardLink from "./LatestVanguardLink";

const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-rc-charcoal">
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.12 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <img src={IMG.heroNight} alt="Ranger Construction night paving" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-rc-charcoal/85 via-rc-charcoal/40 to-transparent" />
      </motion.div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full pt-24 pb-24"
      >
        <motion.div variants={item} className="mb-6">
          <LatestVanguardLink />
        </motion.div>
        <motion.div variants={item} className="font-mono text-rc-sand text-xs md:text-sm uppercase tracking-[0.3em] mb-6">
          Central & South Florida · Road Building & Asphalt
        </motion.div>

        <motion.h1
          variants={item}
          className="font-heading font-black text-white leading-[0.92] tracking-tight text-[clamp(2.8rem,8vw,7rem)] max-w-5xl"
        >
          BUILDING THE
          <br />
          ROADS THAT MOVE
          <br />
          <span className="text-rc-red">FLORIDA</span> FORWARD
        </motion.h1>

        <motion.p variants={item} className="mt-8 text-white/80 text-lg md:text-xl max-w-2xl font-medium">
          Heavy highway construction, asphalt paving and FOB asphalt sales — from Orlando to the Keys.
          Trusted by FDOT and built on a culture of quality and safety.
        </motion.p>

        <motion.div variants={item} className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link
            to="/services/heavy-highway-infrastructure"
            data-testid="hero-capabilities-button"
            onClick={() => trackEvent("cta_click", { label: "hero_explore_capabilities" })}
            className="group bg-rc-red text-white px-8 py-4 rounded-full font-bold uppercase tracking-[0.15em] text-sm hover:bg-rc-reddark transition-colors flex items-center justify-center gap-3"
          >
            Explore Capabilities
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/asphalt-plants" data-testid="hero-asphalt-button" onClick={() => trackEvent("cta_click", { label: "hero_asphalt_fob_sales" })} className="border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold uppercase tracking-[0.15em] text-sm hover:bg-white hover:text-rc-charcoal transition-colors text-center">Asphalt Plants & FOB Sales</Link>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2">
        <span className="font-mono text-white/50 text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent" />
      </div>
    </section>
  );
}
